import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

// 3P
import { useTranslation } from 'react-i18next';

// Shared
import Configs from '@/Configs';
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';
import { handleApiError } from '@/common/utils/handleAPIerrors';
import { useGetPublicSingleRecognition } from '@/queries/useGetPublicSingleRecognition';
import Loader from '@/common/support/loader';
import NoneditableEditor from '@/widgets/noneditableEditor';

// ---------------------------------------------------------------------------------------------------
function RecognitionPublicSingleRecognition() {
  const { t } = useTranslation();

  const { setToasterMessage } = useData();
  const { correlationId } = useAuth();
  const { id } = useParams<{ id: string }>();

  const recognitionID = useMemo(() => {
    if (id) return id;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    return url.split('/').pop() ?? '';
  }, [id]);

  const {
    data: recognition,
    isPending: isLoading,
    isError,
    error,
  } = useGetPublicSingleRecognition(
    { recognitionID, correlationId },
    { enabled: Boolean(recognitionID) }
  );

  useEffect(() => {
    if (!isError || !error) return;
    const m = error.message?.match(/^publicSingleRecognition:(\d+)$/);
    if (m) {
      const handled = handleApiError(Number(m[1]), t);
      if (handled.isError) {
        setToasterMessage({ type: Configs.errorToaster, message: handled.errorMessage });
      }
    } else {
      setToasterMessage({ type: Configs.errorToaster, message: t('error') });
    }
  }, [error, isError, setToasterMessage, t]);

  return (
    <div
      className="flex flex-col lg:p-20 p-4 h-screen bg-gradient-to-b from-green-200 to-white dark:from-slate-900 dark:to-slate-950"
      data-testid="recognition-public-single"
    >
      {isLoading ? (
        <Loader />
      ) : recognition ? (
        <div className="flex flex-col mt-2 md:ml-2 m-1 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg shadow p-8 dark:text-slate-100">
          <h2 className="flex flex-wrap text-lg">
            <span className="flex font-bold text-green-800 dark:text-green-400 mr-1">
              {recognition?.recognizedEmployees != null &&
              Array.isArray(recognition.recognizedEmployees)
                ? (recognition.recognizedEmployees as { name?: string; surname?: string }[]).map(
                    (employee, index, arr) =>
                      index === arr.length - 1 ? (
                        <div key={index}>
                          {employee?.name} {employee?.surname}
                        </div>
                      ) : (
                        <div key={index} className=" mx-1">
                          {employee?.name} {employee?.surname},{' '}
                        </div>
                      )
                  )
                : null}
            </span>
            {' ' + t('recognition.card.public.after-names') + ' '}
          </h2>
          <span className="flex title-description ml-5">
            {recognition?.timestamp != null
              ? `${new Date(recognition.timestamp as string | number).getDate()}/${
                  new Date(recognition.timestamp as string | number).getMonth() + 1
                }/${new Date(recognition.timestamp as string | number).getFullYear()}`
              : null}
          </span>

          <div className="flex flex-col justify-left space-y-4">
            <div className="flex text-sm">
              <NoneditableEditor content={recognition?.recognition as string | undefined} />
            </div>

            <div className="flex flex-col items-start  ml-4 title-description">
              {t('recognition.card.public.demonstrating-values')}
              {recognition?.values != null &&
              Array.isArray(recognition.values) &&
              (recognition.values as unknown[]).length > 0 ? (
                <div className="flex flex-wrap space-x-2 px-4 py-4">
                  {(recognition.values as string[]).map((value, index) => (
                    <div
                      key={index}
                      id={String(index)}
                      className={
                        ' bg-green-200 dark:bg-green-900/40 text-sm  font-bold rounded-lg px-3 py-2'
                      }
                    >
                      {value}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default RecognitionPublicSingleRecognition;
